import { CodingLayout } from "@/components/CodingLayout";
import { AppErrorBoundary } from "@/components/ErrorBoundary";
import { Thread } from "@/components/assistant-ui/thread";
import { Button } from "@/components/ui/button";
import { AIService } from "@/gen/omnios/v1/ai_connect";
import { CommunicationService } from "@/gen/omnios/v1/communication_connect";
import { ConversationKind } from "@/gen/omnios/v1/communication_pb";
import { PartyService } from "@/gen/omnios/v1/party_connect";
import { PartyKind } from "@/gen/omnios/v1/party_pb";
import { StoreService } from "@/gen/omnios/v1/store_connect";
import { useAgentStore } from "@/lib/agent";
import { transport } from "@/lib/grpc";
import { useAgentChat } from "@/lib/use-agent-chat";
import { cn } from "@/lib/utils";
import { AssistantRuntimeProvider } from "@assistant-ui/react";
import { createClient } from "@connectrpc/connect";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/portals/$id/build")({
	component: () => (
		<AppErrorBoundary>
			<PortalBuild />
		</AppErrorBoundary>
	),
});

const storeClient = createClient(StoreService, transport);
const aiClient = createClient(AIService, transport);
const partyClient = createClient(PartyService, transport);
const commClient = createClient(CommunicationService, transport);

type InitStep =
	| "loading"
	| "resolving_agent"
	| "creating_session"
	| "cloning_template"
	| "ready"
	| "error";

const STEP_LABELS: Record<InitStep, string> = {
	loading: "step_loading",
	resolving_agent: "step_resolving_agent",
	creating_session: "step_creating_session",
	cloning_template: "step_cloning_template",
	ready: "step_ready",
	error: "step_error",
};

function PortalBuild() {
	const { t } = useTranslation("portals");
	const { id } = useParams({ from: "/portals/$id/build" });
	const setSessionId = useAgentStore((s) => s.setSessionId);
	const setLayoutMode = useAgentStore((s) => s.setLayoutMode);
	const setPreviewOpen = useAgentStore((s) => s.setPreviewOpen);
	const qc = useQueryClient();
	const navTo = useNavigate();

	const [agentPartyId, setAgentPartyId] = useState("");
	const [conversationId, setConversationId] = useState("");
	const [_sessionId, setLocalSessionId] = useState("");
	const [step, setStep] = useState<InitStep>("loading");
	const [errorMsg, setErrorMsg] = useState("");
	const initRef = useRef(false);

	const { data: portal } = useQuery({
		queryKey: ["portal", id],
		queryFn: () => storeClient.getPortalInstance({ id }),
	})

	// Step 1: resolve agent party
	useEffect(() => {
		setStep("resolving_agent");
		(async () => {
			try {
				const res = await partyClient.listParties({
					kind: PartyKind.AGENT,
					page: { pageSize: 1, pageToken: "" },
				})
				if (res.parties.length > 0) setAgentPartyId(res.parties[0].id);
			} catch (e) {
				setStep("error");
				setErrorMsg(e instanceof Error ? e.message : t("agent_not_found"));
			}
		})()
	}, []);

	// Step 2: init session
	useEffect(() => {
		if (!portal || initRef.current) return;
		// agentPartyId may be empty — backend auto-creates during createAgentSession
		initRef.current = true;

		(async () => {
			// Reuse existing session
			if (portal.activeSessionId) {
				setStep("cloning_template");
				setLocalSessionId(portal.activeSessionId);
				setSessionId(portal.activeSessionId);
				try {
					const ses = await aiClient.getAgentSession({
						id: portal.activeSessionId,
					})
					if (ses.conversationId) setConversationId(ses.conversationId);
					if (ses.agentPartyId && !agentPartyId) setAgentPartyId(ses.agentPartyId);
				} catch {}
				setStep("ready");
				return
			}

			// Create new session
			setStep("creating_session");
			try {
				const conv = await commClient.createConversation({
					subject: portal.name,
					kind: ConversationKind.AGENT_THREAD,
				})
				setConversationId(conv.id);

				const sesRes = await aiClient.createAgentSession({
					label: portal.name,
					portalInstanceId: id,
					conversationId: conv.id,
				})

				const sid = sesRes.session?.id ?? "";
				// Backend auto-creates agent party — capture the ID
				if (sesRes.session?.agentPartyId && !agentPartyId) {
					setAgentPartyId(sesRes.session.agentPartyId);
				}
				if (sid) {
					setStep("cloning_template");
					setLocalSessionId(sid);
					setSessionId(sid);

					await storeClient.updatePortalInstance({ id, status: "building" });
					qc.invalidateQueries({ queryKey: ["portal", id] });
				}
				setStep("ready");
			} catch (e) {
				setStep("error");
				setErrorMsg(
					e instanceof Error ? e.message : t("session_create_failed"),
				)
			}
		})()
	}, [portal, agentPartyId, id, setSessionId, qc]);

	// Force split layout
	useEffect(() => {
		setLayoutMode("split");
		setPreviewOpen(true);
	}, [setLayoutMode, setPreviewOpen]);

	// Show progress during init
	if (step !== "ready") {
		return (
			<InitProgress
				step={step}
				errorMsg={errorMsg}
				onRetry={() => {
					initRef.current = false;
					setStep("loading");
					setErrorMsg("");
				}}
				onBack={() => navTo({ to: "/portals/$id", params: { id } })}
			/>
		)
	}

	return (
		<div
			className="h-full overflow-hidden"
		>
			<CodingLayout
				chatSlot={
					<ChatPanel
						agentPartyId={agentPartyId}
						conversationId={conversationId}
					/>
				}
			/>
		</div>
	)
}

// ── Init progress ──

function InitProgress({
	step,
	errorMsg,
	onRetry,
	onBack,
}: {
	step: InitStep;
	errorMsg: string;
	onRetry: () => void;
	onBack: () => void;
}) {
	const { t } = useTranslation("portals");
	const steps: InitStep[] = [
		"resolving_agent",
		"creating_session",
		"cloning_template",
	]
	const currentIdx = steps.indexOf(step);
	const isError = step === "error";

	return (
		<div className="flex items-center justify-center h-full bg-gradient-to-b from-background to-muted/20">
			<div className="w-80">
				{/* Progress bar */}
				<div className="h-1 bg-muted rounded-full mb-8 overflow-hidden">
					<div
						className={cn(
							"h-full rounded-full transition-all duration-700 ease-out",
							isError ? "bg-destructive" : "bg-primary",
						)}
						style={{
							width: isError
								? "100%"
								: `${((currentIdx + 1) / steps.length) * 100}%`,
						}}
					/>
				</div>

				{/* Current step label */}
				<div className="text-center mb-8">
					{isError ? (
						<>
							<AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-3" />
							<p className="text-sm font-medium text-destructive">
								{t(STEP_LABELS.error)}
							</p>
							<p className="text-xs text-muted-foreground mt-1">{errorMsg}</p>
						</>
					) : (
						<>
							<Loader2 className="h-8 w-8 text-primary mx-auto mb-3 animate-spin" />
							<p className="text-sm font-medium">{t(STEP_LABELS[step])}</p>
						</>
					)}
				</div>

				{/* Steps */}
				<div className="space-y-3">
					{steps.map((s, i) => {
						const isDone = currentIdx > i;
						const isCurrent = s === step;
						return (
							<div
								key={s}
								className={cn(
									"flex items-center gap-3 transition-opacity duration-300",
									!isDone && !isCurrent && "opacity-40",
								)}
							>
								<div
									className={cn(
										"h-5 w-5 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300",
										isDone
											? "bg-emerald-500/15 text-emerald-500"
											: isCurrent
												? "bg-primary/15 text-primary"
												: "bg-muted text-muted-foreground",
									)}
								>
									{isDone ? (
										<Check className="h-3 w-3" />
									) : isCurrent ? (
										<div className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
									) : (
										<div className="h-1.5 w-1.5 rounded-full bg-current opacity-30" />
									)}
								</div>
								<span
									className={cn(
										"text-xs transition-colors duration-300",
										isCurrent ? "text-foreground" : "text-muted-foreground",
									)}
								>
									{t(STEP_LABELS[s])}
								</span>
							</div>
						)
					})}
				</div>

				{/* Error actions */}
				{isError && (
					<div className="flex gap-2 justify-center mt-6">
						<Button size="sm" variant="outline" onClick={onBack}>
							{t("back")}
						</Button>
						<Button size="sm" onClick={onRetry}>
							{t("retry")}
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}

// ── Chat panel ──

function ChatPanel({
	agentPartyId,
	conversationId,
}: {
	agentPartyId: string;
	conversationId: string;
}) {
	const { runtime } = useAgentChat({ agentPartyId, conversationId });

	return (
		<AssistantRuntimeProvider runtime={runtime}>
			<Thread />
		</AssistantRuntimeProvider>
	)
}
